<div class="card bg-white relative border-none checkout-dialog">
  <!-- Header -->
  <div class="card-header p-5 flex">
    <div class="w-full flex items-center justify-center">
      <p class="flex mb-0 pb-0 pr-0 text-primaryDark text-lg sm:text-xl font-bold text-center">
        <span i18n>Potrdi naročnino</span>
      </p>
    </div>
    <button *ngIf="!data.nonDismissible" (click)="cancel()"
            class="btn border-transparent shadow-none text-gray hover:text-grayDark fadeInFadeOut absolute top-0 right-0"
            [disabled]="isProcessing">
      <i class="fas fa-times text-lg"></i>
    </button>
  </div>

  <!-- Content -->
  <div class="card-content overflow-auto p-5" style="max-height: 70vh">
    <!-- Loading State -->
    <div *ngIf="isLoading" class="flex justify-center py-8">
      <app-spinner></app-spinner>
    </div>

    <!-- Error State -->
    <div *ngIf="!isLoading && errorMessage && !pricingBreakdown" class="text-center py-6">
      <div class="text-red-500 mb-4">
        <i class="fas fa-exclamation-circle text-3xl"></i>
      </div>
      <p class="text-sm text-gray-600">{{ errorMessage }}</p>
      <button class="btn btn-primary mt-4" (click)="loadPricingPreview()" i18n>Poskusi znova</button>
    </div>

    <!-- Pricing Content -->
    <ng-container *ngIf="!isLoading && pricingBreakdown">
      <!-- Package Summary -->
      <div class="order-summary mb-6 text-center">
        <h3 class="text-xl font-bold text-primaryDark mb-1">{{ packageName }}</h3>
        <span class="text-sm text-gray-500">{{ subscriptionLabel }}</span>
      </div>

      <!-- Module Description -->
      <div class="module-description mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg" *ngIf="packageDescription">
        <p class="text-sm text-blue-900 flex items-start">
          <i class="fas fa-info-circle text-blue-600 mr-2 mt-0.5"></i>
          <span>{{ packageDescription }}</span>
        </p>
      </div>

      <!-- Billing Period Toggle (module packages with both monthly + yearly prices) -->
      <div class="billing-period-toggle mb-6" *ngIf="showPeriodToggle">
        <div class="inline-flex w-full rounded-lg border border-gray-200 overflow-hidden">
          <button type="button"
                  class="flex-1 py-2 px-3 text-sm font-medium transition-colors"
                  [class.bg-primaryDark]="selectedPeriod === 'monthly'"
                  [class.text-white]="selectedPeriod === 'monthly'"
                  [class.bg-white]="selectedPeriod !== 'monthly'"
                  [class.text-gray-700]="selectedPeriod !== 'monthly'"
                  (click)="onPeriodChange('monthly')"
                  [disabled]="isProcessing"
                  i18n>
            Mesečno
          </button>
          <button type="button"
                  class="flex-1 py-2 px-3 text-sm font-medium transition-colors"
                  [class.bg-primaryDark]="selectedPeriod === 'yearly'"
                  [class.text-white]="selectedPeriod === 'yearly'"
                  [class.bg-white]="selectedPeriod !== 'yearly'"
                  [class.text-gray-700]="selectedPeriod !== 'yearly'"
                  (click)="onPeriodChange('yearly')"
                  [disabled]="isProcessing">
            <span i18n>Letno</span>
            <span *ngIf="data.modulePackage?.yearlyDiscountPct"
                  class="ml-1 text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">
              −{{ data.modulePackage?.yearlyDiscountPct }}%
            </span>
          </button>
        </div>
      </div>

      <!-- Pricing Breakdown -->
      <div class="pricing-breakdown bg-gray-50 rounded-lg p-4 mb-6">
        <!-- Current Package Credit -->
        <div class="line-item flex justify-between items-start py-2 border-b border-gray-200"
             *ngIf="pricingBreakdown.currentPackageCredit > 0">
          <span class="text-sm text-gray-600" i18n>Dobropis iz trenutnega paketa</span>
          <div class="text-right">
            <span class="text-sm font-medium text-green-600">
              -{{ pricingBreakdown.currentPackageCredit | number:'1.2-2' }} {{ pricingBreakdown.currency }}
            </span>
            <span *ngIf="pricingBreakdown.taxRate > 0" class="block text-xs text-gray-400">
              -{{ (pricingBreakdown.currentPackageCredit * (1 + pricingBreakdown.taxRate / 100)) | number:'1.2-2' }} {{ pricingBreakdown.currency }} <span i18n>z DDV</span>
            </span>
          </div>
        </div>

        <!-- New Package Cost (prorated) -->
        <div class="line-item flex justify-between items-start py-2 border-b border-gray-200"
             *ngIf="!pricingBreakdown.isDowngradeToFree">
          <span class="text-sm text-gray-600">
            {{ packageName }}
            <span *ngIf="pricingBreakdown.currentPackageCredit > 0 || pricingBreakdown.proratedAmount < pricingBreakdown.newPackageCost"
                  class="text-xs text-gray-400" i18n>(sorazmerno)</span>
          </span>
          <div class="text-right">
            <span class="text-sm font-medium">
              {{ pricingBreakdown.proratedAmount | number:'1.2-2' }} {{ pricingBreakdown.currency }}
            </span>
            <span *ngIf="pricingBreakdown.taxRate > 0" class="block text-xs text-gray-400">
              {{ (pricingBreakdown.proratedAmount * (1 + pricingBreakdown.taxRate / 100)) | number:'1.2-2' }} {{ pricingBreakdown.currency }} <span i18n>z DDV</span>
            </span>
          </div>
        </div>

        <!-- Per-vehicle rate. CarPeak charges nothing up front (BRIEFD-7435), so
             without this the summary reads "0,00 EUR" with no price anywhere. -->
        <div class="line-item flex justify-between items-start py-2 border-b border-gray-200"
             *ngIf="pricingBreakdown.isModulePackage && pricingBreakdown.newPackage?.perVehicle">
          <span class="text-sm text-gray-600" i18n>Nato na vozilo</span>
          <div class="text-right">
            <span class="text-sm font-medium">
              {{ (pricingBreakdown.newPackage!.volumeDeal?.perVehicle ?? pricingBreakdown.newPackage!.perVehicle) | number:'1.2-2' }}
              {{ pricingBreakdown.currency }}
            </span>
            <span *ngIf="pricingBreakdown.newPackage!.volumeDeal" class="block text-xs text-gray-400">
              <span i18n>za prvih</span> {{ pricingBreakdown.newPackage!.volumeDeal!.units }}
              <span i18n>vozil, nato</span>
              {{ pricingBreakdown.newPackage!.perVehicle | number:'1.2-2' }} {{ pricingBreakdown.currency }}
            </span>
          </div>
        </div>

        <!-- Total -->
        <div class="line-item total flex justify-between py-3 mt-2">
          <span *ngIf="pricingBreakdown.taxRate > 0" class="text-base font-bold text-primaryDark" i18n>Skupaj z DDV</span>
          <span *ngIf="pricingBreakdown.taxRate === 0" class="text-base font-bold text-primaryDark" i18n>Skupaj</span>
          <span class="text-base font-bold text-primaryDark">
            <ng-container *ngIf="pricingBreakdown.finalAmount >= 0; else showCredit">
              {{ pricingBreakdown.finalAmount | number:'1.2-2' }} {{ pricingBreakdown.currency }}
            </ng-container>
            <ng-template #showCredit>
              <span class="text-green-600" i18n>Dobropis: {{ (-pricingBreakdown.finalAmount) | number:'1.2-2' }} {{ pricingBreakdown.currency }}</span>
            </ng-template>
          </span>
        </div>
      </div>

      <!-- Next Billing Date -->
      <div class="billing-info text-center mb-6" *ngIf="pricingBreakdown.nextBillingDate && !pricingBreakdown.isDowngradeToFree">
        <p class="text-xs text-gray-500">
          <span i18n>Naslednji obračun:</span>
          <span class="font-medium ml-1">{{ pricingBreakdown.nextBillingDate | date:'d. M. yyyy' }}</span>
        </p>
      </div>

      <!-- Manual billing notice (Stripe disabled for this account) -->
      <div class="payment-method mb-6 p-3 bg-blue-50 rounded-lg" *ngIf="stripeDisabled">
        <div class="flex items-center">
          <i class="far fa-file-invoice text-blue-600 mr-2"></i>
          <span class="text-sm" i18n>Plačilo bo obračunano preko računa (brez kartice).</span>
        </div>
      </div>

      <!-- Existing Payment Method Info -->
      <div class="payment-method mb-6 p-3 bg-blue-50 rounded-lg" *ngIf="!stripeDisabled && existingPaymentMethod && !useNewCard">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="far fa-credit-card text-blue-600 mr-2"></i>
            <span class="text-sm">
              {{ existingPaymentMethod.brand | titlecase }} **** {{ existingPaymentMethod.last4 }}
            </span>
          </div>
          <div class="flex items-center gap-3">
            <span class="text-xs text-gray-500">
              {{ existingPaymentMethod.exp_month }}/{{ existingPaymentMethod.exp_year }}
            </span>
            <button
              class="text-xs text-blue-600 hover:text-blue-800 underline"
              (click)="onChangeCard()"
              [disabled]="isProcessing"
              i18n>
              Zamenjaj
            </button>
          </div>
        </div>
        <p class="text-xs text-gray-600 mt-2" *ngIf="data.type === 'module_package' && !stripeDisabled" i18n>
          Modul se bo ob podaljšanju naročnine zaračunal na to kartico.
        </p>
      </div>

      <!-- New Card Notice (when changing card) -->
      <div class="payment-method mb-6 p-3 bg-yellow-50 rounded-lg" *ngIf="!stripeDisabled && useNewCard">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="far fa-credit-card text-yellow-600 mr-2"></i>
            <span class="text-sm" i18n>Nova kartica bo avtorizirana ob potrditvi</span>
          </div>
          <button
            *ngIf="existingPaymentMethod"
            class="text-xs text-blue-600 hover:text-blue-800 underline"
            (click)="onCancelCardChange()"
            [disabled]="isProcessing"
            i18n>
            Uporabi obstoječo
          </button>
        </div>
        <p class="text-xs text-gray-600 mt-2" *ngIf="data.type === 'module_package' && !stripeDisabled" i18n>
          Modul se bo ob podaljšanju naročnine zaračunal na to kartico.
        </p>
      </div>

      <!-- Inline Error -->
      <div class="error-message mb-4 p-3 bg-red-50 rounded-lg" *ngIf="errorMessage">
        <p class="text-sm text-red-600 flex items-center">
          <i class="fas fa-exclamation-circle mr-2"></i>
          {{ errorMessage }}
        </p>
      </div>

      <!-- Upgrade Proration Info -->
      <div class="info-message mb-4 p-3 bg-blue-50 rounded-lg" *ngIf="pricingBreakdown.isUpgrade && pricingBreakdown.isModulePackage">
        <p class="text-sm text-blue-700 flex items-start">
          <i class="fas fa-info-circle mr-2 mt-0.5"></i>
          <span i18n>Trenutno obračunsko obdobje bo sorazmerno zaračunano, nato pa bo ustvarjeno novo naročniško obdobje z novimi cenami.</span>
        </p>
      </div>

      <!-- Active Subscription Module Warning (product package change) -->
      <div class="info-message mb-4 p-3 bg-blue-50 rounded-lg"
           *ngIf="!pricingBreakdown.isModulePackage && pricingBreakdown.activeSubscriptionModules?.length">
        <p class="text-sm text-blue-700 flex items-start">
          <i class="fas fa-info-circle mr-2 mt-0.5"></i>
          <span i18n>Ob menjavi paketa bo za aktivni modul ({{ pricingBreakdown.activeSubscriptionModules[0].name }}) sorazmerno obračunano trenutno obdobje in ustvarjeno novo z novimi cenami.</span>
        </p>
      </div>

      <!-- Downgrade Warning -->
      <div class="warning-message mb-4 p-3 bg-yellow-50 rounded-lg" *ngIf="pricingBreakdown.isDowngradeToFree">
        <p class="text-sm text-yellow-700" i18n>
          <i class="fas fa-exclamation-triangle mr-2"></i>
          Preklapljate na brezplačni paket. Nekatere funkcionalnosti bodo omejene.
        </p>
      </div>

      <!-- Actions -->
      <div class="actions flex justify-center gap-4">
        <button *ngIf="!data.nonDismissible" class="btn btn-lg border-none text-grayDark" (click)="cancel()" [disabled]="isProcessing" i18n>
          Prekliči
        </button>
        <button *ngIf="data.nonDismissible" class="btn btn-lg border-none text-grayDark" (click)="goBack()" i18n>
          Nazaj na nastavitve
        </button>
        <button class="btn btn-primary btn-lg" (click)="confirmPurchase()" [disabled]="isProcessing">
          <span *ngIf="!isProcessing">
            <ng-container *ngIf="pricingBreakdown.finalAmount > 0; else freeActivation" i18n>
              Potrdi plačilo
            </ng-container>
            <ng-template #freeActivation>
              <span i18n>Potrdi aktivacijo</span>
            </ng-template>
          </span>
          <app-spinner *ngIf="isProcessing"></app-spinner>
        </button>
      </div>
    </ng-container>
  </div>
</div>

<!-- Payment Authorization Modal -->
<app-payment-authorization-modal
  *ngIf="showPaymentModal && stripeClientSecret && stripePublishableKey"
  [stripeClientSecret]="stripeClientSecret"
  [stripePublishableKey]="stripePublishableKey"
  [paymentType]="data.type"
  [ref]="data.ref"
  (paymentComplete)="onPaymentComplete($event)"
  (paymentCancelled)="onPaymentCancelled()"
  (paymentFailed)="onPaymentFailed($event)">
</app-payment-authorization-modal>
